import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { registerHomeTile } from '../stores/homescreen';
import { createApiClient } from '@room/frontend-sdk';

const apiClient = createApiClient({
  baseURL: '/api',
});

type PluginRegistryEntry = {
  pluginId: string;
  state: 'enabled' | 'disabled';
};

type PipelineItem = {
  id: string;
  name?: string;
  description?: string;
  status: string;
  kind: string;
  enabled?: boolean;
  manifest?: {
    type?: string;
    ui?: {
      sidebar?: {
        icon?: string;
        label?: string;
      };
    };
  };
};

type PipelineSnapshot = {
  items: PipelineItem[];
  counts: {
    total: number;
    enabled: number;
    disabled: number;
    installed: number;
    review: number;
  };
};

/**
 * Fetches user's plugin registry (enabled/disabled state)
 */
const fetchUserRegistry = async (): Promise<PluginRegistryEntry[]> => {
  try {
    const response = await apiClient.get<{ data?: { plugins: PluginRegistryEntry[] } }>('/plugins/registry');
    return response?.data?.plugins ?? [];
  } catch (error) {
    console.warn('[usePluginRegistry] Failed to fetch user registry:', error);
    return [];
  }
};

/**
 * Fetches pipeline snapshot (all installed plugins)
 */
const fetchPipelineSnapshot = async (): Promise<PipelineSnapshot | null> => {
  try {
    const response = await apiClient.get<{ data?: PipelineSnapshot }>('/developer/pipeline');
    return response?.data ?? null;
  } catch (error) {
    console.warn('[usePluginRegistry] Failed to fetch pipeline snapshot:', error);
    return null;
  }
};

/**
 * Hook to fetch and auto-register enabled plugins as home tiles
 */
export function usePluginRegistry() {
  const {
    data: userRegistry = [],
    isLoading: isLoadingRegistry,
  } = useQuery({
    queryKey: ['plugin-registry'],
    queryFn: fetchUserRegistry,
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000,
  });

  const {
    data: pipelineSnapshot,
    isLoading: isLoadingPipeline,
  } = useQuery({
    queryKey: ['pipeline-snapshot'],
    queryFn: fetchPipelineSnapshot,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // Auto-register enabled plugins as home tiles
  useEffect(() => {
    if (!pipelineSnapshot || !userRegistry) return;

    const enabledPluginIds = new Set(
      userRegistry
        .filter((entry) => entry.state === 'enabled')
        .map((entry) => entry.pluginId)
    );

    pipelineSnapshot.items
      .filter((plugin) => enabledPluginIds.has(plugin.id))
      .forEach((plugin) => {
        const icon = plugin.manifest?.ui?.sidebar?.icon ?? '🧩';
        const label = plugin.manifest?.ui?.sidebar?.label ?? plugin.name ?? plugin.id;

        registerHomeTile({
          id: `plugin-${plugin.id}`,
          label,
          icon,
          route: `/plugins/${plugin.id}`,
          section: 'main',
          order: 100, // Plugins appear after core apps
          visible: () => {
            // Check if plugin is still enabled in real-time
            const registry = userRegistry;
            const entry = registry.find((e) => e.pluginId === plugin.id);
            return entry?.state === 'enabled';
          },
        });
      });
  }, [pipelineSnapshot, userRegistry]);

  return {
    userRegistry,
    pipelineSnapshot,
    isLoading: isLoadingRegistry || isLoadingPipeline,
    enabledPlugins: userRegistry.filter((entry) => entry.state === 'enabled'),
  };
}
